import React from "react";
import { Box, Typography, Chip, Button, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { CheckCheck, ExternalLink, ShieldCheck, MessageCircle } from "lucide-react";
import { format } from "date-fns";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    root: {
      backgroundColor: isDark ? "#111b21" : "#efeae2",
      backgroundImage: isDark
        ? "radial-gradient(#202c33 1px, transparent 1px)"
        : "radial-gradient(#d1d7db 1px, transparent 1px)",
      backgroundSize: "16px 16px",
      borderRadius: 12,
      padding: theme.spacing(2.5),
      border: `1px solid ${isDark ? "#222e35" : "#e0e0e0"}`,
      marginBottom: theme.spacing(2)
    },
    metaHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 8,
      marginBottom: theme.spacing(1.5),
      paddingBottom: theme.spacing(1),
      borderBottom: `1px dashed ${isDark ? "#2a3942" : "#d1d7db"}`
    },
    bubbleContainer: {
      display: "flex",
      justifyContent: "flex-end",
      marginTop: theme.spacing(1)
    },
    bubble: {
      maxWidth: "88%",
      minWidth: 260,
      backgroundColor: isDark ? "#005c4b" : "#d9fdd3",
      color: isDark ? "#e9edef" : "#111b21",
      borderRadius: "12px 12px 2px 12px",
      padding: "10px 14px 8px 14px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
      position: "relative",
      wordBreak: "break-word"
    },
    senderLabel: {
      fontSize: "0.75rem",
      fontWeight: 700,
      color: isDark ? "#25d366" : "#075e54",
      marginBottom: 4,
      display: "flex",
      alignItems: "center",
      gap: 4
    },
    messageText: {
      fontSize: "0.9rem",
      lineHeight: 1.5,
      whiteSpace: "pre-wrap",
      fontFamily: "inherit"
    },
    actionButtonWrapper: {
      marginTop: 10,
      paddingTop: 8,
      borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)"}`
    },
    actionButton: {
      width: "100%",
      backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.75)",
      color: isDark ? "#53bdeb" : "#027eb5",
      fontWeight: 600,
      textTransform: "none",
      borderRadius: 8,
      fontSize: "0.85rem",
      "&:hover": {
        backgroundColor: isDark ? "rgba(255,255,255,0.16)" : "#ffffff"
      }
    },
    bubbleFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 4,
      marginTop: 6
    },
    timestamp: {
      fontSize: "0.7rem",
      color: isDark ? "#8696a0" : "#667781"
    },
    ticks: {
      color: "#53bdeb",
      display: "inline-flex"
    },
    templateTag: {
      fontSize: "0.72rem",
      height: 22,
      fontWeight: 600,
      backgroundColor: isDark ? "#202c33" : "#e9edef",
      color: isDark ? "#aebac1" : "#54656f"
    }
  };
});

const DisparoWhatsAppPreview = ({ disparo }) => {
  const classes = useStyles();

  if (!disparo) return null;

  // Extrair texto da mensagem
  const textoMensagem =
    disparo.texto_mensagem ||
    disparo.payload_envio?.body ||
    disparo.template_corpo ||
    "Mensagem de notificação do processo de admissão.";

  // Formatar hora
  let horaFormatada = "";
  try {
    const rawDate = disparo.data_hora_formatada || disparo.created_at;
    if (rawDate) {
      if (/^\d{2}\/\d{2}\/\d{4}\s+(\d{2}:\d{2})/.test(rawDate)) {
        const match = rawDate.match(/^\d{2}\/\d{2}\/\d{4}\s+(\d{2}:\d{2})/);
        horaFormatada = match ? match[1] : "";
      } else {
        const parsed = new Date(rawDate);
        if (!isNaN(parsed.getTime())) {
          horaFormatada = format(parsed, "HH:mm");
        }
      }
    }
  } catch (e) {}

  const templateName =
    disparo.template_name ||
    disparo.payload_envio?.templateName ||
    "Template Meta Oficial";

  const linkAssinatura = disparo.link_assinatura || disparo.gisign_envelopes?.link_assinatura;

  return (
    <Box className={classes.root}>
      {/* Cabeçalho de Metadados da Meta */}
      <Box className={classes.metaHeader}>
        <Box display="flex" alignItems="center" gap={1}>
          <MessageCircle size={16} color="#25d366" />
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            WhatsApp Oficial (Meta Business Cloud API)
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Identificador do Template Oficial homologado na Meta">
            <Chip
              size="small"
              label={`Template: ${templateName}`}
              className={classes.templateTag}
            />
          </Tooltip>
          <Tooltip title="Disparo processado e entregue com sucesso">
            <Chip
              size="small"
              icon={<ShieldCheck size={14} color="#16a34a" />}
              label="Entrega Confirmada"
              style={{
                backgroundColor: "rgba(22, 163, 74, 0.12)",
                color: "#16a34a",
                fontWeight: 600,
                fontSize: "0.72rem",
                height: 22
              }}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* Balão do WhatsApp */}
      <Box className={classes.bubbleContainer}>
        <Box className={classes.bubble}>
          {/* Rótulo de Remetente no balão */}
          <Box className={classes.senderLabel}>
            <span>Timbre RH • Notificação Oficial</span>
          </Box>

          {/* Texto do Template da Mensagem */}
          <Typography className={classes.messageText}>
            {textoMensagem}
          </Typography>

          {/* Botão Interativo do Template (Link GiSign se presente) */}
          {linkAssinatura && (
            <Box className={classes.actionButtonWrapper}>
              <Button
                component="a"
                href={linkAssinatura}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<ExternalLink size={15} />}
                className={classes.actionButton}
              >
                Abrir Link de Assinatura
              </Button>
            </Box>
          )}

          {/* Rodapé do Balão: Horário e Dois Tiques Azuis */}
          <Box className={classes.bubbleFooter}>
            {horaFormatada && (
              <Typography className={classes.timestamp}>
                {horaFormatada}
              </Typography>
            )}
            <Tooltip title="Entregue com sucesso (Dois tiques de confirmação)">
              <span className={classes.ticks}>
                <CheckCheck size={16} />
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DisparoWhatsAppPreview;
